import React, { SyntheticEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  InputLabel,
  Modal,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { isEmpty } from "lodash";

import {
  AutoComplete,
  ButtonStyle,
  CompareNpiTextBox,
  CompareNpiTextStyle,
  ExportIcon,
  FaArrowRightArrowLeftIcon,
  ScorecardModalContainer,
  ScorecardModalHeaderBox,
  ScorecardModalTitle,
  IconFaDownload,
  PeriodText,
  ScorecardBox,
  TotalScoreBox,
  TotalScoreStyle,
  FiAlertCircleIcon,
  QualifierMessage,
  CloseButton,
  ScorecardDisclaimer,
  styleForPDF,
  styleForPeriodPDF,
  baselineHeader,
} from "../styles/style";
import fontWeight from "../../../styles/mui/fontWeight";
import { colors } from "../../../styles/colors";
import {
  displayFlex,
  displayFlexEnd,
  flexAllCentre,
  flexRow,
} from "../../../styles/mui/styles/display";
import PerformanceCriteria from "./PerformanceCriteria";
import {
  Disclaimer,
  getEngagementPerformanceCriteriaData,
  getInterOperabilityPerformanceCriteriaData,
  getQualityPerformanceCriteriaData,
  GetScorecardDetailsActionDispatchTypes,
  getScorecardKeys,
  ModalHeader,
  pushErrorMessages,
  ScorecardConstants,
} from "../../../constants/Scorecard";
import { PerformancePeriods, QuarterRange, ScorecardBaselineTable, ScorecardDetails } from "../../../models/Scorecard";
import {
  createPdf,
  formatPercentageValue,
  getCurrnetPageFullPath,
  getPerformancePeriodsWithRange,
  getUaserPageViewPayload,
  getValue,
} from "../../../utils";
import { FormAutocompleteList } from "../../../components/FormAutocompleteList";
import useMenuItem from "../../../hooks/useMenuItem";
import { ServiceProvider } from "../../../models/ServiceProvider";
import { MyAccountModel } from "../../../models/MyAccount";
import { ApplicationTitles, defaultListData, UserRole } from "../../../constants/HomePage";
import { ListOptions } from "../../../models/Master";
import { Numbers } from "../../../constants/Referral";
import { getAgencyList, getAgencyListForTangoAdmin } from "../../../app/common";
import CompareNpiModal from "../containers/CompareNpiModal";
import { ButtonContained } from "../../../app/common/button";
import toastType from "../../../utils/SnackbarProvider";
import {
  MessageType,
  ResponseMessages,
} from "../../../constants/ResponseMessage";
import { UserPageTrack } from "../../../models/User";
import { getValidMessageType } from "../../../constants/User";
import { redirectUri } from "../../../constants/Config";
import BaselineTable from "./BaselineTable";

export interface PropsFromState {
  scorecardData: ScorecardDetails | null;
  scorecardError: any;
  masterServiceProviderList: ServiceProvider[];
  loggedInUser: MyAccountModel;
  masterServiceProviderLoading: boolean;
  excelDownloadState: Blob;
  excelDownloadError: any;
  scorecardErrorResponse: any;
  compareNPIData: any;
  scorecardBaselineTableState: ScorecardBaselineTable | null;
}
export interface PropsFromDispatch {
  getScorecardDetails: (providerNpi: string, quarterRange: QuarterRange, performancePeriod?: string) => void;
  getMasterServiceProvider: () => void;
  resetStates: (actionType: string) => void;
  getCompareNPIDetails: (userId: string) => void;
  excelDownload: (npi: string, quarterRange: QuarterRange) => void;
  postUserPageTrack: (payload: UserPageTrack) => void;
  getScorecardBaselineTable: (providerState: string, quarterRange: QuarterRange, pcmNpi: string) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const Scorecard: React.FC<AllProps> = ({
  getScorecardDetails,
  scorecardData,
  masterServiceProviderList,
  loggedInUser,
  resetStates,
  masterServiceProviderLoading,
  getMasterServiceProvider,
  excelDownload,
  excelDownloadError,
  scorecardError,
  compareNPIData,
  postUserPageTrack,
  getScorecardBaselineTable,
  scorecardBaselineTableState
}) => {
  const [serviceProviderList, setServiceProviderList] = useState<ListOptions[]>(
    []
  );
  const [agencyProvider, setAgencyProvider] =
    useState<ListOptions>(defaultListData);
  const [agencyDropdownList, setAgencyDropdownList] = useState<ListOptions[]>(
    []
  );
  const [isOpenCompareNpiModal, setIsOpenCompareNpiModal] =
    useState<boolean>(false);
  const { open, onOpen, onClose } = useMenuItem();
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<any>([]);
  const initialPeriods = getPerformancePeriodsWithRange();
  const [performancePeriods, setPerformancePeriods] = useState<PerformancePeriods[]>(initialPeriods);
  const [performancePeriodSelected, setPerformancePeriodSelected] = useState<PerformancePeriods>(initialPeriods[0]);
  const [quarterRange, setQuarterRange] = useState<QuarterRange>(initialPeriods[0].quarterRange);
  const { open: openQuarter, onOpen: onOpenQuarter, onClose: onCloseQuarter } = useMenuItem();
  
  const setAgencyList = () => {
    if (isEmpty(agencyDropdownList)) {
      setAgencyDropdownList(
        loggedInUser.providerPortalUser.roleType !== UserRole.TANGO_ADMIN
          ? getAgencyList(masterServiceProviderList, loggedInUser, true)
          : getAgencyListForTangoAdmin(masterServiceProviderList, true)
      );
    }
  };
  const [isErrorAvailable, setIsErrorAvailable] = useState<boolean>(false);
  const [isHide, setIsHide] = useState<boolean>(false);

  useEffect(() => {
    if (masterServiceProviderList?.length > Numbers.ZERO) {
      setAgencyList();
    }
  }, [loggedInUser]);

  const methods = useForm<any>({
    defaultValues: {
      providerNpi: "",
    },
  });
  const { control, setValue, getValues, handleSubmit } = methods;

  useEffect(() => {
    const periods = getPerformancePeriodsWithRange();

    setPerformancePeriods(periods);
    setPerformancePeriodSelected(periods[0]);
    setQuarterRange(periods[0].quarterRange);

    setValue("performancePeriod", periods[0].value);
  }, []);

  const gridRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (masterServiceProviderList && masterServiceProviderList.length > 0) {
      setAgencyList();
    }
  }, [masterServiceProviderList]);

  useEffect(() => {
    if (scorecardError) {
      setIsErrorAvailable(true);
    }
  },[scorecardError])

  useEffect(() => {
    if (
      agencyDropdownList?.length > Numbers.ZERO &&
      !masterServiceProviderLoading
    ) {
      const npi =
        masterServiceProviderList &&
        masterServiceProviderList.filter(
          (item: ServiceProvider) =>
            item.serviceProviderId === agencyDropdownList[Numbers.ZERO].value
        )[0].providerNpi;
      setValue("providerNpi", npi);

      const state =
        masterServiceProviderList &&
        masterServiceProviderList.filter(
          (item: ServiceProvider) =>
            item.serviceProviderId === agencyDropdownList[Numbers.ZERO].value
        )[0].providerState;
        setValue("providerState", state);
      getScorecardDetails(npi, quarterRange, performancePeriodSelected.label);
      getScorecardBaselineTable(state, quarterRange, npi);
    }
  }, [agencyDropdownList]);

  useEffect(() => {
    setServiceProviderList(agencyDropdownList);
    if (
      masterServiceProviderList &&
      masterServiceProviderList.length > 0 &&
      agencyDropdownList &&
      agencyDropdownList.length > 0
    ) {
      setValue(
        "providerNpi",
          masterServiceProviderList.filter(
            (item: ServiceProvider) =>
              item.serviceProviderId === agencyDropdownList[Numbers.ZERO].value
          )[0].providerNpi
      );
      setValue("providerId", agencyDropdownList[Numbers.ZERO].value);
      setAgencyProvider(agencyDropdownList[Numbers.ZERO]);
    }
  }, [agencyDropdownList]);

  useEffect(() => {
    if (
      serviceProviderList &&
      serviceProviderList.length < 1 &&
      masterServiceProviderList &&
      masterServiceProviderList.length > 0
    ) {
      setAgencyList();
    }
    if (isEmpty(masterServiceProviderList)) {
      getMasterServiceProvider();
    }
    postUserPageTrack(
      getUaserPageViewPayload(
        getValidMessageType(ApplicationTitles.SCORECARD),
        getCurrnetPageFullPath(),
        redirectUri,
        ""
      )
    );
  }, []);

  const onSubmit = (data: any) => {
    const { providerNpi, providerState } = data;
    getScorecardDetails(providerNpi, quarterRange, performancePeriodSelected.label);
    getScorecardBaselineTable(providerState, quarterRange, providerNpi);
  };

  const handleAgencyChange = (
    _e: SyntheticEvent<Element, Event>,
    value: ListOptions
  ) => {
    setAgencyProvider(value);
    setValue("providerId", Number(value.value));

    const selectedServiceProvider = masterServiceProviderList.find(
      (v: ServiceProvider) => v.serviceProviderId === value.value
    );

    if (selectedServiceProvider) {
      setValue("providerNpi", selectedServiceProvider.providerNpi);
      setValue("providerState", selectedServiceProvider.providerState);
      handleSubmit(onSubmit)();
    }
  };

  const handlePerformancePeriodChange = (
    _e: SyntheticEvent<Element, Event>,
    value: PerformancePeriods
  ) => {
    setPerformancePeriodSelected(value);
    setQuarterRange(value.quarterRange);

    setValue("performancePeriod", value.value);

    const data = getValues();
    getScorecardDetails( data.providerNpi, value.quarterRange, value.label );
    getScorecardBaselineTable( data.providerState, value.quarterRange, data.providerNpi );
  };

  const getMissingQualifiers = (scorecardData: any) => {
    const result = [];
    for (const key in scorecardData) {
      if (getScorecardKeys(key) && (scorecardData[key] <= 0)) {
        result.push(key);
      }
    }
    return result;
  };

  const setError = (scorecardData: ScorecardDetails | null) => {
    if (scorecardData && Number(scorecardData.displayData) === 1) {//added as per Tk-15457
      setIsErrorAvailable(false);
    } else {
      setIsErrorAvailable(true);
    }
  }

  useEffect(() => {
    if (scorecardData !== null) {
      const tempErrorMessages: any = [];
      const missingQualifiers = getMissingQualifiers(scorecardData);
      missingQualifiers &&
        missingQualifiers.map((item: string) => {
          tempErrorMessages.push(pushErrorMessages(item));
          return tempErrorMessages;
        });
      if (tempErrorMessages && tempErrorMessages.length > Numbers.ZERO) {
        setErrorMessages(tempErrorMessages);
        setError(scorecardData);
        if (Number(scorecardData.displayData) !== Numbers.ONE) {
          setIsOpenModal(true);
        }
      } else {
        setError(scorecardData);
      }
    }
  }, [scorecardData]);

  useEffect(() => {
    if (!isEmpty(compareNPIData)) {
      setIsOpenCompareNpiModal(false);
    }
  }, [compareNPIData]);

  useEffect(() => {
    if (
      excelDownloadError &&
      excelDownloadError.error &&
      Object.keys(excelDownloadError.error).length === Numbers.ZERO
    ) {
      toastType.error(ResponseMessages(MessageType.EXCE_DOWNLOAD_FAILURE));
    }
  }, [excelDownloadError]);

  useEffect(() => {
    return () => {
      resetStates(
        GetScorecardDetailsActionDispatchTypes.GET_SCORECARD_DETAILS_RESET
      );
      setErrorMessages(null);
    };
  }, []);


  const generatePdf = () => {
    setTimeout(() => {
      createPdf(
        gridRef,
        `Scorecard_of_NPI_${getValues().providerNpi}.pdf`
      );
      setIsHide(false);
    }, 20);
  };

  const autoCompleteValue = (data: string) => {
    const value = data.split("\n");
    return (
      <Box sx={styleForPDF}>
        <Typography
          variant="h6"
          fontWeight={fontWeight[4]}
          color={colors.fonts[25]}
          pt={"0.4rem"}
        >
          {value[0]}
        </Typography>
        <Typography
          variant="h6"
          fontWeight={fontWeight[4]}
          color={colors.fonts[25]}
          pt={"0.2rem"}
        >
          {value[1]}
        </Typography>
      </Box>
    );
  };

  const autoCompleteValuePeriod = (data: string) => {
    return (
      <Box sx={styleForPeriodPDF}>
        <Typography
          variant="h6"
          fontWeight={fontWeight[4]}
          color={colors.fonts[25]}
        >
          {data}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={ScorecardBox}>
      <Grid container>
        <Grid size={{ xs: 8 }}>
          <Typography
            variant="h3"
            color={colors.black[17]}
            fontWeight={fontWeight[5]}
            m={"0.6rem 0"}
          >
            SCORECARD DETAILS
          </Typography>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Box sx={displayFlexEnd}>
            <Grid container sx={flexRow}>
              <Grid sx={{ display: "flex" }}>
                {!isErrorAvailable && (
                  <>
                    <Box sx={ExportIcon}>
                      <Button
                        sx={ButtonStyle()}
                        onClick={() => excelDownload(getValues().providerNpi, quarterRange)}
                        data-testid="btnDownload-excel"
                      >
                        <IconFaDownload />
                        <Typography
                          variant="subtitle2"
                          color={colors.white[1]}
                          fontWeight={fontWeight[5]}
                        >
                          Excel
                        </Typography>
                      </Button>
                    </Box>
                    <Box sx={ExportIcon}>
                      <Button
                        sx={ButtonStyle()}
                        onClick={() => {
                          setIsHide(true);
                          generatePdf();
                        }}
                        data-testid="btnDownload-pdf"
                      >
                        <IconFaDownload />
                        <Typography
                          variant="subtitle2"
                          color={colors.white[1]}
                          fontWeight={fontWeight[5]}
                        >
                          PDF
                        </Typography>
                      </Button>
                    </Box>
                  </>
                )}
                {loggedInUser.providerPortalUser.roleType ===
                  UserRole.PROVIDER_ADMIN && (
                  <Box sx={CompareNpiTextBox}>
                    <FaArrowRightArrowLeftIcon />
                    <Typography
                      variant="subtitle1"
                      fontWeight={fontWeight[6]}
                      color={colors.portalColor[30]}
                      sx={CompareNpiTextStyle}
                      onClick={() => {
                        setIsOpenCompareNpiModal(true);
                      }}
                    >
                      Compare NPIs
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }} ref={gridRef}>
          <Grid container sx={flexRow}>
            {!isHide ? (
              <>
                <Grid size={{ xs: 6 }} pt={"1.2rem"}>
                  <InputLabel shrink={true} sx={displayFlex}>
                    <Typography
                      variant="h2"
                      fontWeight={fontWeight[5]}
                      color={colors.fonts[2]}
                      pl={"0.8rem"}
                    >
                      LOCATION
                    </Typography>
                  </InputLabel>
                  <Box sx={displayFlex}>
                    <Grid size={{ xs: 12 }}>
                      <FormAutocompleteList
                        control={control}
                        list={serviceProviderList}
                        value={agencyProvider}
                        variant="standard"
                        sx={AutoComplete}
                        open={open}
                        onOpen={onOpen}
                        onClose={onClose}
                        handleChange={handleAgencyChange}
                        isScorecard={true}
                      />
                    </Grid>
                  </Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={fontWeight[5]}
                    color={colors.fonts[4]}
                    sx={PeriodText}
                  >
                    PERFORMANCE PERIOD:
                  </Typography>
                  <Box sx={displayFlex}>
                    <Grid size={{ xs: 12 }}>
                      <FormAutocompleteList
                        control={control}
                        list={performancePeriods}
                        value={performancePeriodSelected}
                        variant="standard"
                        sx={AutoComplete}
                        open={openQuarter}
                        onOpen={onOpenQuarter}
                        onClose={onCloseQuarter}
                        handleChange={handlePerformancePeriodChange}
                        isScorecard={false}
                        disableFilter={true}
                      />
                    </Grid>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6 }} pt={"1.2rem"}>
                  <Typography
                    variant="h6"
                    fontWeight={fontWeight[5]}
                    color={colors.fonts[4]}
                    sx={baselineHeader}
                  >
                    {`Baseline Table for ${getValues().providerState}`}
                  </Typography>
                  <BaselineTable
                    scorecardBaselineTableState={scorecardBaselineTableState}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid size={{ xs: 6 }} pt={"2rem"}>
                  <InputLabel shrink={true} sx={displayFlex}>
                    <Typography
                      variant="h2"
                      fontWeight={fontWeight[5]}
                      color={colors.fonts[2]}
                      pl={"0.8rem"}
                    >
                      LOCATION
                    </Typography>
                  </InputLabel>
                  <Box sx={displayFlex}>
                    <Grid size={{ xs: 12 }}>
                      {autoCompleteValue(agencyProvider.label)}
                    </Grid>
                  </Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={fontWeight[5]}
                    color={colors.fonts[4]}
                    sx={PeriodText}
                  >
                    PERFORMANCE PERIOD:
                  </Typography>
                  <Box sx={displayFlex}>
                    <Grid size={{ xs: 12 }}>
                    {autoCompleteValuePeriod(performancePeriodSelected.label)}
                    </Grid>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6 }} pt={"3.5rem"}>
                  <Typography
                    variant="h6"
                    fontWeight={fontWeight[5]}
                    color={colors.fonts[4]}
                    sx={baselineHeader}
                  >
                    {`Baseline Table for ${getValues().providerState}`}
                  </Typography>
                  <BaselineTable
                    scorecardBaselineTableState={scorecardBaselineTableState}
                  />
                </Grid>
              </>
            )}
              <Grid size={{ xs: 12 }} sx={TotalScoreStyle}>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 7.7 }}>
                    <Typography
                      variant="h2"
                      color={colors.black[17]}
                      fontWeight={fontWeight[4]}
                      p={"1rem 0"}
                    >
                      TOTAL
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 4.3 }}>
                    <Box sx={TotalScoreBox}>
                      {renderTotalScoreBox(
                        ScorecardConstants.TOTAL_POINTS_EARNED,
                        getValue(scorecardData, "totalBranchScore", 0),
                        isErrorAvailable
                      )}
                      {renderTotalScoreBox(
                        ScorecardConstants.TOTAL_MAX_POINTS,
                        getValue(scorecardData, "totalMaxScore", 0),
                        isErrorAvailable
                      )}
                      {renderTotalScoreBox(
                        ScorecardConstants.TOTAL_AVERAGE_SCORE,
                        formatPercentageValue(
                          getValue(scorecardData, "totalAverageScore", 0)
                        ),
                        isErrorAvailable
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
           <Grid size={{ xs: 12 }} pb={"1rem"}>
              <PerformanceCriteria
                performanceCriteriaData={getEngagementPerformanceCriteriaData(
                  scorecardData
                )}
                isErrorAvailable={isErrorAvailable}
              />
              <PerformanceCriteria
                performanceCriteriaData={getQualityPerformanceCriteriaData(
                  scorecardData
                )}
                isErrorAvailable={isErrorAvailable}
              />
              <PerformanceCriteria
                performanceCriteriaData={getInterOperabilityPerformanceCriteriaData(
                  scorecardData
                )}
                isErrorAvailable={isErrorAvailable}
              />
            </Grid>
            </Grid>
          </Grid>
        </Grid>
        {isOpenCompareNpiModal && (
          <CompareNpiModal
            isOpenModal={isOpenCompareNpiModal}
            setIsOpenModal={setIsOpenCompareNpiModal}
          />
        )}
        {!isEmpty(errorMessages) && errorMessages.length > 0 && (
          <AlertModal
            isOpenModal={isOpenModal}
            errorMessages={errorMessages}
            handleClose={() => setIsOpenModal(false)}
          />
        )}
      </Box>
  )
};

const renderTotalScoreBox = (label: string, value: number | string, isErrorAvailable: boolean) => {
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={fontWeight[3]}>
        {label}
      </Typography>
      <Typography
        variant="h4"
        fontWeight={fontWeight[6]}
        align="right"
        pt={"0.7rem"}
      >
        {isErrorAvailable === false ? value : "-"}
      </Typography>
    </Box>
  );
};

interface AlertModalProps {
  isOpenModal: boolean;
  handleClose: () => void;
  errorMessages: string[];
}
const AlertModal: React.FC<AlertModalProps> = ({
  isOpenModal,
  handleClose,
  errorMessages,
}) => (
  <Modal
    open={isOpenModal}
    aria-labelledby="modal-modal-title"
    aria-describedby="modal-modal-description"
    sx={flexAllCentre}
  >
    <Grid container sx={ScorecardModalContainer}>
      <Grid size={{ xs: 11 }}>
        <Typography
          variant="h2"
          color={colors.black[17]}
          fontWeight={fontWeight[6]}
          sx={ScorecardModalTitle}
        >
          Unable to display chosen scorecard
        </Typography>
      </Grid>
      <Grid size={{ xs: 11.5 }} sx={ScorecardModalHeaderBox}>
        <Typography
          variant="subtitle1"
          color={colors.black[17]}
          fontWeight={fontWeight[4]}
          sx={ScorecardModalTitle}
        >
          {ModalHeader}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }}>
        {errorMessages.map((qualifierMessage: string) => (
          <>
            <Box sx={displayFlex} pb={"0.7rem"}>
                <Grid size={{ xs: 0.5 }}>
                  <FiAlertCircleIcon />
                </Grid>
                <Grid size={{ xs: 11.5 }} >
                  <Typography
                    variant="subtitle1"
                    color={colors.red[100]}
                    fontWeight={fontWeight[4]}
                    sx={QualifierMessage}
                  >
                    {qualifierMessage}
                  </Typography>
                </Grid>
            </Box>
          </>
        ))}
      </Grid>
      <Grid size={{ xs: 11.5 }} sx={ScorecardModalHeaderBox}>
        <Typography
          variant="subtitle2"
          color={colors.black[17]}
          fontWeight={fontWeight[4]}
          sx={ScorecardDisclaimer}
        >
          {Disclaimer}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }} pr={"1rem"}>
        <Box sx={displayFlexEnd}>
          <ButtonContained
            id="btnClose"
            onClick={handleClose}
            label="Close"
            style={CloseButton}
          />
        </Box>
      </Grid>
    </Grid>
  </Modal>
);
export default Scorecard;
